import { useState } from 'react';
import { useApp } from '../store/app';
import { useDrive } from '../store/drive';
import { useTheme } from '../store/theme';
import { getAccessToken, getClientId, hasClientId, setClientId, signOut } from '../auth/gis';
import { activateDemo, activateDrive, forgetDrive } from '../lib/sources';
import { deviceLabel } from '../lib/localStore';
import { makeEntry } from '../lib/progress';
import { serviceWorkerError, serviceWorkerSupported } from '../lib/swBridge';
import type { DriveRoot } from '../drive/source';
import type { ThemeChoice } from '../lib/theme';
import { NmButton, NmCard, NmPill } from './nm';
import { FolderPicker } from './FolderPicker';
import { AlertIcon, BackIcon, CloudIcon } from './icons';

const THEME_OPTIONS: { value: ThemeChoice; label: string }[] = [
   { value: 'system', label: 'System' },
   { value: 'light', label: 'Light' },
   { value: 'dark', label: 'Dark' },
];

export function Settings() {
   const navigate = useApp((s) => s.navigate);

   return (
      <div className="settings">
        <div className="player__head">
          <NmButton onClick={() => navigate({ name: 'library' })} size="sm">
             <BackIcon size={18} />
             Library
          </NmButton>
        </div>

        <DriveCard />
        <AppearanceCard />
        <LibraryCard />
        <DemoTools />
      </div>
   );
}

/* ------------------------------------------------------------------ *
  * Google Drive
  * ------------------------------------------------------------------ */

function DriveCard() {
   const status = useDrive((s) => s.status);
   const error = useDrive((s) => s.error);
   const root = useDrive((s) => s.root);
   const needsSignIn = useDrive((s) => s.needsSignIn);

   const [clientId, setLocalClientId] = useState(getClientId());
   const [saved, setSaved] = useState(false);
   const [picking, setPicking] = useState(false);
   const [connectError, setConnectError] = useState<string | null>(null);

   const configured = hasClientId();

   const saveId = () => {
      setClientId(clientId);
      setSaved(true);
      setConnectError(null);
      window.setTimeout(() => setSaved(false), 2000);
   };

   const openPicker = async () => {
      setConnectError(null);
      try {
        // Force the interactive flow so the consent screen appears on first use.
        await getAccessToken({ interactive: true });
        setPicking(true);
      } catch (err) {
        setConnectError(err instanceof Error ? err.message : String(err));
      }
   };

   const choose = (chosen: DriveRoot) => {
      setPicking(false);
      void activateDrive(chosen);
   };

   const disconnect = () => {
      signOut();
      forgetDrive();
      void activateDemo();
   };

   return (
      <NmCard className="settings__card">
        <h2 className="settings__title">Google Drive</h2>

        {!serviceWorkerSupported() ?
          <p className="settings__warn">
             <AlertIcon size={17} /> This browser cannot run a service worker over the current
          connection. Serve the app over HTTPS (or localhost) for proper streaming — otherwise
          Drive files have to be downloaded in full before they play.
        </p>
      : serviceWorkerError() ?
        <p className="settings__warn">
          <AlertIcon size={17} /> The background streaming worker did not start, so Drive files
          will be downloaded in full instead of streamed, and large ones will refuse to play.
          Reloading usually fixes it. ({serviceWorkerError()})
        </p>
      : null}

      <label className="field">
        <span className="field__label">OAuth Client ID</span>
        <input
           className="nm-inset nm-xs field__input"
           type="text"
           inputMode="url"
           spellCheck={false}
           autoComplete="off"
           placeholder="1234567890-abc123.apps.googleusercontent.com"
           value={clientId}
           onChange={(e) => setLocalClientId(e.target.value)}
        />
      </label>
      <p className="settings__hint">
        Created once in Google Cloud Console — the README has the exact steps. It is stored only
        on this device, which is what lets the same build run anywhere.
      </p>
      <div className="panel__row">
        <NmButton onClick={saveId} disabled={clientId.trim() === getClientId()}>
           {saved ? 'Saved' : 'Save Client ID'}
        </NmButton>
      </div>

      {configured && (
        <>
           <hr className="rule" />
           <dl className="kv">
             <dt>Status</dt>
             <dd>
                <CloudIcon size={15} />
                {status === 'connected' ? 'Connected'
                : status === 'connecting' ? 'Connecting…'
                : status === 'error' ? 'Failed'
                : needsSignIn ? 'Signed out'
                : 'Not connected'}
             </dd>
             {root && (
                <>
                   <dt>Folder</dt>
                   <dd>{root.name}</dd>
                </>
             )}
           </dl>

           {(error ?? connectError) && (
              <p className="settings__warn">
                <AlertIcon size={17} /> {connectError ?? error}
              </p>
           )}

          <div className="panel__row">
            <NmButton className="nmbtn--primary" onClick={() => void openPicker()}>
              {root ? 'Change folder' : 'Connect Google Drive'}
            </NmButton>
            {root && <NmButton onClick={disconnect}>Disconnect</NmButton>}
          </div>
        </>
      )}

      {picking && <FolderPicker onCancel={() => setPicking(false)} onChoose={choose} />}
    </NmCard>
   );
}

/* ------------------------------------------------------------------ *
  * Appearance / library
  * ------------------------------------------------------------------ */

function AppearanceCard() {
   const choice = useTheme((s) => s.choice);
   const setChoice = useTheme((s) => s.setChoice);

   return (
      <NmCard className="settings__card">
      <h2 className="settings__title">Appearance</h2>
      <p className="settings__hint">
        Both themes are tuned so text stays readable — the softness is in the shadows, not the
        type.
      </p>
      <div className="panel__row">
        {THEME_OPTIONS.map((opt) => (
           <NmPill key={opt.value} active={choice === opt.value} onClick={() => setChoice(opt.value)}>
             {opt.label}
           </NmPill>
        ))}
      </div>
    </NmCard>
   );
}

function LibraryCard() {
   const source = useApp((s) => s.source);
   const syncStatus = useApp((s) => s.syncStatus);
   const syncError = useApp((s) => s.syncError);
   const bookCount = useApp((s) => s.books.length);

   return (
      <NmCard className="settings__card">
        <h2 className="settings__title">Library</h2>
        <dl className="kv">
          <dt>Source</dt>
          <dd>{source?.label ?? 'Not connected'}</dd>
          <dt>Books</dt>
          <dd>{bookCount}</dd>
          <dt>This device</dt>
          <dd>{deviceLabel()}</dd>
          <dt>Sync</dt>
          <dd>
            <CloudIcon size={15} />
            {syncStatus === 'error' ? `Failed — ${syncError}` : syncStatusLabel(syncStatus)}
          </dd>
        </dl>
      </NmCard>
   );
}

function syncStatusLabel(status: string): string {
   switch (status) {
      case 'syncing':
        return 'Syncing…';
      case 'saved':
        return 'Up to date';
      default:
        return 'Idle';
   }
}

/* ------------------------------------------------------------------ *
  * Demo tools
  * ------------------------------------------------------------------ */

/**
  * Test affordances for demo mode. "Simulate another device" writes a progress
  * entry straight into the demo library's remote store with a newer timestamp —
  * exactly the state a second device would leave behind, and the quickest honest
  * way to check the resume prompt and per-book merge.
  */
function DemoTools() {
   const source = useApp((s) => s.source);
   const books = useApp((s) => s.books);
   const currentBookId = useApp((s) => s.currentBookId);
   const positionSec = useApp((s) => s.positionSec);
   const syncFromRemote = useApp((s) => s.syncFromRemote);

   if (source?.kind !== 'demo') return null;

   const simulate = async () => {
      if (!source) return;
      const target = books.find((b) => b.id === currentBookId) ?? books[0];
      if (!target) return;

      const remote = (await source.loadProgress()) ?? { version: 1 as const, books: {} };
      // Far enough from wherever you are to clear the 30 s prompt threshold.
      const elsewhere = positionSec > 120 ? 12 : positionSec + 95;
      remote.books[target.id] = makeEntry(0, elsewhere, 0, 'iPhone Safari');
      await source.saveProgress(remote);
      await syncFromRemote();
   };

    const clear = async () => {
       await source.saveProgress({ version: 1, books: {} });
       location.reload();
    };

    return (
       <NmCard className="settings__card">
         <h2 className="settings__title">Demo tools</h2>
         <p className="settings__hint">
           You are on the built-in demo library. These exist so the cross-device behaviour can be
           exercised on one machine.
         </p>
         <div className="panel__row">
           <NmButton onClick={() => void simulate()}>Simulate another device</NmButton>
           <NmButton onClick={() => void clear()}>Reset all progress</NmButton>
         </div>
       </NmCard>
    );
}
